<template>
  <div class="modal" v-if="config" @keydown.esc.stop="onEscape" @keydown.tab="onTab" @focusin="onFocusInOut" @focusout="onFocusInOut">
    <component v-if="currentModalComponent" :is="currentModalComponent"></component>
    <modal-inner v-else aria-label="Dialog">
      <div class="modal__content" v-html="simpleModal.contentHtml(config)"></div>
      <div class="modal__button-bar">
        <button class="button" v-if="simpleModal.rejectText" @click="config.reject()">{{simpleModal.rejectText}}</button>
        <button class="button button--resolve" v-if="simpleModal.resolveText" @click="config.resolve()">{{simpleModal.resolveText}}</button>
      </div>
    </modal-inner>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import simpleModals from '../data/simpleModals';
import editorSvc from '../services/editorSvc';

import ModalInner from './modals/common/ModalInner.vue';
import FilePropertiesModal from './modals/FilePropertiesModal.vue';
import SettingsModal from './modals/SettingsModal.vue';
import TemplatesModal from './modals/TemplatesModal.vue';
import HtmlExportModal from './modals/HtmlExportModal.vue';
import LinkModal from './modals/LinkModal.vue';
import ImageModal from './modals/ImageModal.vue';

const getTabbables = (container) => {
  if (!container || typeof container.querySelectorAll !== 'function') {
    return [];
  }
  return container.querySelectorAll('a[href], button, .textfield, input[type=checkbox]')
    // Filter enabled and visible element
    .cl_filter(el => !el.disabled && el.offsetParent !== null && !el.classList.contains('not-tabbable'));
};

export default {
  components: {
    ModalInner,
    FilePropertiesModal,
    SettingsModal,
    TemplatesModal,
    HtmlExportModal,
    LinkModal,
    ImageModal,
  },
  computed: {
    ...mapGetters('modal', [
      'config',
    ]),
    currentModalComponent() {
      if (this.config.type) {
        let componentName = this.config.type[0].toUpperCase();
        componentName += this.config.type.slice(1);
        componentName += 'Modal';
        if (this.$options.components[componentName]) {
          return componentName;
        }
      }
      return null;
    },
    simpleModal() {
      return simpleModals[this.config.type] || {};
    },
  },
  methods: {
    onEscape() {
      this.config.reject();
      editorSvc.clEditor.focus();
    },
    onTab(evt) {
      const tabbables = getTabbables(this.$el);
      const firstTabbable = tabbables[0];
      const lastTabbable = tabbables[tabbables.length - 1];
      if (evt.shiftKey && firstTabbable === evt.target) {
        evt.preventDefault();
        lastTabbable.focus();
      } else if (!evt.shiftKey && lastTabbable === evt.target) {
        evt.preventDefault();
        firstTabbable.focus();
      }
    },
    onFocusInOut(evt) {
      const { parentNode } = evt.target;
      if (parentNode && parentNode.parentNode) {
        // Focus effect
        if (parentNode.classList.contains('form-entry__field')
          && parentNode.parentNode.classList.contains('form-entry')) {
          parentNode.parentNode.classList.toggle(
            'form-entry--focused',
            evt.type === 'focusin',
          );
        }
      }
    },
  },
  mounted() {
    this.$watch(
      () => this.config,
      (isOpen) => {
        if (isOpen) {
          const tabbables = getTabbables(this.$el);
          if (tabbables[0]) {
            tabbables[0].focus();
          }
        }
      },
      { immediate: true },
    );
  },
};
</script>

<style lang="scss">
@import '../styles/variables.scss';

.modal {
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: rgba(160, 160, 160, 0.5);
  overflow: auto;
  z-index: 10;

  p {
    line-height: 1.5;
  }
}

.modal__inner-1 {
  margin: 0 auto;
  width: 100%;
  min-width: 320px;
  max-width: 480px;
}

.modal__inner-2 {
  margin: 40px 10px 100px;
  background-color: #f8f8f8;
  padding: 50px 50px 40px;
  border-radius: $border-radius-base;
  position: relative;

  .app--dark & {
    background-color: #2d2d2d;
    color: rgba(255, 255, 255, 0.87);
  }

  .app--sepia & {
    background-color: #f4ecd8;
    color: #5b4636;
  }

  .app--nord & {
    background-color: #3b4252;
    color: #d8dee9;
  }

  .app--solarized-light & {
    background-color: #fdf6e3;
    color: #657b83;
  }

  .app--solarized-dark & {
    background-color: #073642;
    color: #93a1a1;
  }

  .app--dracula & {
    background-color: #21222c;
    color: #f8f8f2;
  }

  .app--monokai & {
    background-color: #1e1f1c;
    color: #f8f8f2;
  }

}

.modal__content > :first-child,
.modal__content > .modal__image:first-child + * {
  margin-top: 0;
}

.modal__image {
  float: left;
  width: 60px;
  height: 60px;
  margin: 1.5em 1.2em 0.5em 0;

  & + *::after {
    content: '';
    display: block;
    clear: both;
  }
}

.modal__title {
  font-weight: bold;
  font-size: 1.5rem;
  line-height: 1.4;
  margin-top: 2.5rem;
}

.modal__sub-title {
  opacity: 0.6;
  font-size: 0.75rem;
  margin-bottom: 1.5rem;
}

.modal__error {
  color: #de2c00;
}

.modal__info {
  background-color: $info-bg;
  border-radius: $border-radius-base;
  margin: 1.2em 0;
  padding: 0.75em 1.25em;
  font-size: 0.95em;
  line-height: 1.6;

  pre {
    line-height: 1.5;
  }
}

.modal__info--multiline {
  padding-top: 0.1em;
  padding-bottom: 0.1em;
}

.modal__button-bar {
  margin-top: 2rem;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
}

.form-entry {
  margin: 1em 0;
}

.form-entry__label {
  display: block;
  font-size: 0.9rem;
  color: #808080;

  .form-entry--focused & {
    color: darken($link-color, 10%);
  }

  .form-entry--error & {
    color: darken($error-color, 10%);
  }
}

.form-entry__label-info {
  font-size: 0.75rem;
}

.form-entry__field {
  border: 1px solid #b0b0b0;
  border-radius: $border-radius-base;
  position: relative;
  overflow: hidden;

  .form-entry--focused & {
    border-color: $link-color;
    box-shadow: 0 0 0 2.5px transparentize($link-color, 0.67);
  }

  .form-entry--error & {
    border-color: $error-color;
    box-shadow: 0 0 0 2.5px transparentize($error-color, 0.67);
  }
}

.form-entry__actions {
  text-align: right;
  margin: 0.25em;
}

.form-entry__button {
  width: 38px;
  height: 38px;
  padding: 6px;
  display: inline-block;
  background-color: transparent;
  opacity: 0.75;

  &:active,
  &:focus,
  &:hover {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.1);
  }
}

.form-entry__radio,
.form-entry__checkbox {
  margin: 0.25em 1em;

  input {
    margin-right: 0.25em;
  }
}

.form-entry__info {
  font-size: 0.75em;
  opacity: 0.67;
  line-height: 1.4;
  margin: 0.25em 0;
}

.tabs {
  border-bottom: 1px solid $hr-color;
  margin: 1em 0 2em;

  &::after {
    content: '';
    display: block;
    clear: both;
  }
}

.tabs__tab {
  width: 50%;
  float: left;
  text-align: center;
  line-height: 1.4;
  font-weight: 400;
  font-size: 1.1em;
}

.tabs__tab > a {
  width: 100%;
  text-decoration: none;
  padding: 0.67em 0.33em;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  border-top-left-radius: $border-radius-base;
  border-top-right-radius: $border-radius-base;
  color: $link-color;

  .app--dark & {
    color: rgba(255, 255, 255, 0.7);
  }

  .app--sepia & {
    color: #5b4636;
  }

  .app--nord & {
    color: #88c0d0;
  }

  .app--solarized-light & {
    color: #586e75;
  }

  .app--solarized-dark & {
    color: #93a1a1;
  }

  .app--dracula & {
    color: #bd93f9;
  }

  .app--monokai & {
    color: #66d9ef;
  }

  &:hover,
  &:focus {
    background-color: rgba(0, 0, 0, 0.05);

    .app--dark &,
    .app--nord &,
    .app--solarized-dark &,
    .app--dracula &,
    .app--monokai & {
      background-color: rgba(255, 255, 255, 0.05);
    }
  }
}

.tabs__tab--active > a {
  border-bottom: 2px solid $link-color;
  color: inherit;

  .app--dark & {
    border-bottom-color: rgba(255, 255, 255, 0.7);
  }

  .app--sepia & {
    border-bottom-color: #5b4636;
  }

  .app--nord & {
    border-bottom-color: #88c0d0;
  }

  .app--solarized-light & {
    border-bottom-color: #586e75;
  }

  .app--solarized-dark & {
    border-bottom-color: #93a1a1;
  }

  .app--dracula & {
    border-bottom-color: #bd93f9;
  }

  .app--monokai & {
    border-bottom-color: #66d9ef;
  }
}

// Responsive styles
@media (max-width: 600px) {
  .modal__inner-1 {
    min-width: 280px;
    max-width: 100%;
  }

  .modal__inner-2 {
    margin: 20px 5px 50px;
    padding: 40px 20px 30px;
  }

  .modal__title {
    font-size: 1.25rem;
  }

  .modal__button-bar {
    flex-direction: column;
    gap: 8px;

    .button {
      width: 100%;
      margin: 0;
    }
  }

  .tabs__tab {
    font-size: 0.95em;
  }

  .form-entry__label {
    font-size: 0.85rem;
  }
}

@media (max-width: 400px) {
  .modal__inner-2 {
    margin: 10px 5px 30px;
    padding: 35px 15px 25px;
  }

  .modal__image {
    width: 48px;
    height: 48px;
    margin: 1em 1em 0.5em 0;
  }
}
</style>
